import {
  growthMotionFilterOptions,
  softwareSectorFilterOptions,
  primaryCustomerFilterOptions
} from 'utils/constants';
import { formatCurrency } from './dataFormatters';
import { formatThousands, formatIfMissing } from '.';
import { Info } from 'interfaces/components/dashboardPdf';
import { ICompanyInfo } from 'interfaces/store/companyInfo';
import { FilterOption } from 'interfaces/components/filter';
import { DisabledFilters } from 'interfaces/store/disabledFilters';
import { ChartFilters, ChartSwitches } from 'interfaces/store/chartFilters';

export const getPdfInfo = (companyInfo: ICompanyInfo): Info[] => [
  {
    header: 'Growth and Retention',
    data: [
      {
        label: 'YoY ARR Growth',
        value: `${companyInfo.requiredInfo.yoyARR}%`
      },
      {
        label: 'New Logo ARR',
        value: `${formatIfMissing(
          companyInfo.growthAndRetention.newLogoArrOfGrossNew
        )}%`
      },
      {
        label: 'Expansion ARR',
        value: `${formatIfMissing(
          companyInfo.growthAndRetention.expansionArrOfGrossNew
        )}%`
      },
      {
        label: 'New Logo Velocity',
        value: `${formatIfMissing(
          companyInfo.growthAndRetention.newLogoVelocity
        )}%`
      },
      {
        label: 'Quick Ratio',
        value: `${formatIfMissing(companyInfo.growthAndRetention.quickRatio)}x`
      },
      {
        label: 'Net Dollar Retention',
        value: `${formatIfMissing(
          companyInfo.growthAndRetention.netDollarRetention
        )}%`
      },
      {
        label: 'Gross Dollar Retention',
        value: `${formatIfMissing(
          companyInfo.growthAndRetention.grossDollarRetention
        )}%`
      },
      {
        label: 'Current ARR',
        value: companyInfo.requiredInfo.currentARR
          ? `${formatCurrency(companyInfo.requiredInfo.currentARR)}`
          : ''
      },
      {
        label: 'Annual Revenue',
        value: companyInfo.requiredInfo.revenue
          ? `${formatCurrency(companyInfo.requiredInfo.revenue)}`
          : ''
      },
      {
        label: 'Total Headcount',
        value: String(companyInfo.requiredInfo.totalHeadcount) || ''
      },
      {
        label: 'Average Deal Size',
        value: companyInfo.requiredInfo.avgDealSize
          ? `${formatCurrency(companyInfo.requiredInfo.avgDealSize)}`
          : ''
      }
    ]
  },
  {
    header: 'Operational Efficiency',
    data: [
      {
        label: 'Net Magic Number',
        value: `${formatIfMissing(
          companyInfo.operationalEfficiency.netMagicNumber
        )}x`
      },
      {
        label: 'Rule of 40',
        value: `${formatIfMissing(
          companyInfo.requiredInfo.ruleOf40 ||
            Number(companyInfo.requiredInfo.yoyARR) +
              Number(companyInfo.requiredInfo.fcfMargin)
        )}%`
      },
      {
        label: 'CAC Payback Period',
        value: `${formatIfMissing(
          companyInfo.operationalEfficiency.cacPaybackPeriod
        )} month${
          Number(companyInfo.operationalEfficiency.cacPaybackPeriod) > 1
            ? 's'
            : ''
        }`
      },
      {
        label: 'Burn Multiple',
        value: `${formatIfMissing(
          companyInfo.operationalEfficiency.burnMultiple
        )}x`
      },
      {
        label: 'OpEx as a % of Revenue',
        type: 'multiple',
        value: [
          `G&A: ${formatIfMissing(
            companyInfo.operationalEfficiency.gaPercent
          )}%`,
          `R&D: ${formatIfMissing(
            companyInfo.operationalEfficiency.rdPercent
          )}%`,
          `S&M: ${formatIfMissing(
            companyInfo.operationalEfficiency.smPercent
          )}%`
        ]
      }
    ]
  },
  {
    header: 'Profitability and Cash Flow',
    data: [
      {
        label: 'Gross Margin',
        value: `${formatIfMissing(companyInfo.requiredInfo.grossMargin)}%`
      },
      {
        label: 'FCF Margin',
        value: `${companyInfo.requiredInfo.fcfMargin}%`
      }
    ]
  },
  {
    header: 'Headcount',
    data: [
      {
        label: 'Headcount Distribution',
        type: 'multiple',
        subLabel: '(Total FTEs)',
        value: [
          `G&A: ${formatIfMissing(companyInfo.headCount.gAHeadPercent)}%`,
          `R&D: ${formatIfMissing(companyInfo.headCount.rDHeadPercent)}%`,
          `S&M: ${formatIfMissing(companyInfo.headCount.sMHeadPercent)}%`
        ]
      },
      {
        label: 'ARR per FTE',
        value: `${formatCurrency(
          companyInfo.requiredInfo.arrPerFTE ||
            Math.round(
              Number(companyInfo.requiredInfo.currentARR) /
                Number(companyInfo.requiredInfo.totalHeadcount)
            )
        )}`
      },
      {
        label: 'OpEx per FTE',
        value: `${formatCurrency(companyInfo.headCount.opexPerFTE)}`
      }
    ]
  }
];

export const getPdfStatistics = (companyInfo: ICompanyInfo): Info['data'] => [
  {
    label: 'ARR Growth',
    value: companyInfo.requiredInfo.yoyARR
      ? `${Math.round(companyInfo.requiredInfo.yoyARR * 100) / 100}%`
      : 'N/A'
  },
  {
    label: 'Net Dollar Retention',
    value: companyInfo.growthAndRetention.netDollarRetention
      ? `${
          Math.round(companyInfo.growthAndRetention.netDollarRetention * 100) /
          100
        }%`
      : 'N/A'
  },
  {
    label: 'Rule of 40',
    value: companyInfo.requiredInfo.ruleOf40
      ? `${Math.round(companyInfo.requiredInfo.ruleOf40 * 100) / 100}%`
      : companyInfo.requiredInfo.yoyARR && companyInfo.requiredInfo.fcfMargin
      ? `${
          Math.round(
            (companyInfo.requiredInfo.yoyARR +
              companyInfo.requiredInfo.fcfMargin) *
              100
          ) / 100
        }%`
      : 'N/A'
  },
  {
    label: 'Net Magic Number',
    value: companyInfo.operationalEfficiency.netMagicNumber
      ? `${
          Math.round(companyInfo.operationalEfficiency.netMagicNumber * 100) /
          100
        }x`
      : 'N/A'
  },
  {
    label: 'ARR per FTE',
    value: companyInfo.requiredInfo.arrPerFTE
      ? `${formatThousands(
          Math.round(companyInfo.requiredInfo.arrPerFTE * 100) / 100,
          0
        )}`
      : (companyInfo.requiredInfo.currentARR ||
          companyInfo.requiredInfo.revenue) &&
        companyInfo.requiredInfo.totalHeadcount
      ? `${formatThousands(
          (Math.round(
            (companyInfo.requiredInfo.currentARR ||
              companyInfo.requiredInfo.revenue ||
              0) / companyInfo.requiredInfo.totalHeadcount
          ) *
            100) /
            100,
          0
        )}`
      : 'N/A'
  }
];

export const getPdfFilters = (
  chartFilters: ChartFilters,
  disabledFilters: DisabledFilters,
  chartSwitches: ChartSwitches
): {
  label: string;
  options?: string[];
  value: string[];
  disabled?: boolean;
}[] => [
  {
    label: 'Software Sector',
    disabled: disabledFilters.softwareSector,
    options: softwareSectorFilterOptions.map(o => o.label),
    value: chartFilters.softwareSector.map((c: FilterOption) => c.label)
  },
  {
    label: 'Growth Motion',
    disabled: disabledFilters.growthMotion,
    options: growthMotionFilterOptions.map(o => o.label),
    value: chartFilters.growthMotion.map((c: FilterOption) => c.label)
  },
  {
    label: 'Customer Segment',
    disabled: disabledFilters.primaryCustomer,
    options: primaryCustomerFilterOptions.map(o => o.label),
    value: chartFilters.primaryCustomer.map((c: FilterOption) => c.label)
  },
  {
    label: 'X-Axis',
    value: chartSwitches.xAxisSwitch ? ['ARR Range'] : ['Average Deal Size']
  },
  {
    label: 'Time Period',
    value: chartSwitches.timeDurationSwitch ? ['All time'] : ['Last 4 years']
  },
  {
    label: 'Reference Point',
    value: chartSwitches.quartileMedianSwitch ? ['Median'] : ['Top Quartile']
  }
];
